/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import {
  X,
  Percent,
  Tag,
  Check,
  Edit,
  ArrowLeft,
  ArrowRight,
  Truck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useIsDark from "../../utils/isDark";

Modal.setAppElement("#root");

const PromotionModal = ({
  isOpen,
  onClose,
  products = [],
  onCreatePromotion,
  promotionToEdit = null,
  onUpdatePromotion = null,
}) => {
  const isDark = useIsDark();
  const contentRef = useRef(null);
  const { t, i18n } = useTranslation();

  // États pour la promotion
  const [promotionTitle, setPromotionTitle] = useState("");
  const [promotionSubtitle, setPromotionSubtitle] = useState("");
  const [promotionType, setPromotionType] = useState("discount"); // Nouvel état: 'discount' ou 'freeShipping'
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(10);
  const [triggerProducts, setTriggerProducts] = useState([]);
  const [discountedProducts, setDiscountedProducts] = useState([]);
  const [requireAllProducts, setRequireAllProducts] = useState(false); // Nouvel état
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("trigger");
  const [timeCrossSell, setTimeCrossSell] = useState({
    active: false,
    time: 0,
  });
  const isEditMode = !!promotionToEdit;

  // Styles optimisés pour dark mode (inchangé)
  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "1000px",
      maxWidth: "95%",
      height: "85vh",
      borderRadius: "12px",
      padding: "0",
      border: isDark ? "1px solid #3a3a3c" : "1px solid #e5e7eb",
      boxShadow: isDark
        ? "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)"
        : "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      overflow: "hidden",
      backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
      color: isDark ? "#e5e5e5" : "#1f2937",
    },
    overlay: {
      backgroundColor: isDark ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.5)",
      zIndex: 1000,
      backdropFilter: "blur(2px)",
    },
  };

  useEffect(() => {
    if (isOpen) {
      if (promotionToEdit) {
        setPromotionTitle(promotionToEdit.title || "");
        setPromotionSubtitle(promotionToEdit.subtitle || "");
        setPromotionType(promotionToEdit.promotionType || "discount"); // Charger le type
        setDiscountType(promotionToEdit.discountType || "percentage");
        setDiscountValue(promotionToEdit.discountValue || 10);
        setTriggerProducts(promotionToEdit.triggerProductIds || []);
        setDiscountedProducts(promotionToEdit.discountedProductIds || []);
        setRequireAllProducts(promotionToEdit.requireAllProducts || false); // Charger le statut
        setTimeCrossSell({
          active: !!promotionToEdit.timeCrossSell?.active,
          time: Number(promotionToEdit.timeCrossSell?.time || 0),
        });
      } else {
        setPromotionTitle("");
        setPromotionSubtitle("");
        setPromotionType("discount"); // Type par défaut
        setDiscountType("percentage");
        setDiscountValue(10);
        setTriggerProducts([]);
        setDiscountedProducts([]);
        setRequireAllProducts(false); // Par défaut désactivé
        setTimeCrossSell({ active: false, time: 0 });
      }
      setSearchTerm("");
      setActiveTab("trigger");
    }
  }, [isOpen, promotionToEdit]);

  const truncateText = (text, maxLength = 35) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const filteredProducts = products.filter(
    (product) =>
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const toggleTriggerProduct = (productId) => {
    setTriggerProducts((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const toggleDiscountedProduct = (productId) => {
    setDiscountedProducts((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (activeTab === "trigger") {
      if (triggerProducts.length === filteredProducts.length) {
        setTriggerProducts([]);
      } else {
        setTriggerProducts(filteredProducts.map((p) => p.id));
      }
    } else {
      if (discountedProducts.length === filteredProducts.length) {
        setDiscountedProducts([]);
      } else {
        setDiscountedProducts(filteredProducts.map((p) => p.id));
      }
    }
  };

  const formatPrice = (price) => {
    return `${price?.toLocaleString() || "0"} دج`;
  };

  const calculateDiscountedPrice = (originalPrice) => {
    if (promotionType !== "discount") return originalPrice;

    if (discountType === "percentage") {
      return originalPrice * (1 - discountValue / 100);
    } else {
      return Math.max(0, originalPrice - discountValue);
    }
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p.id === productId);
    return product?.name || "منتج غير معروف";
  };

  const handleSubmitPromotion = () => {
    if (!promotionTitle.trim()) {
      alert(t("cashBackModal.alerts.emptyTitle"));
      return;
    }

    if (triggerProducts.length === 0) {
      alert(t("crossSellModal.alerts.emptyTrigger"));
      return;
    }

    if (discountedProducts.length === 0) {
      alert(t("crossSellModal.alerts.emptyDiscounted"));
      return;
    }

    if (promotionType === "discount") {
      if (discountValue <= 0) {
        alert(t("crossSellModal.alerts.invalidDiscount"));
        return;
      }

      if (discountType === "fixed" && discountValue <= 0) {
        alert(t("crossSellModal.alerts.invalidFixed"));
        return;
      }
    }

    const promotionData = {
      title: promotionTitle,
      subtitle: promotionSubtitle,
      promotionType, // Ajout du type
      discountType: promotionType === "discount" ? discountType : null,
      discountValue: promotionType === "discount" ? Number(discountValue) : 0,
      triggerProductIds: triggerProducts,
      discountedProductIds: discountedProducts,
      requireAllProducts, // Ajout de la condition
      createdAt: isEditMode ? promotionToEdit.createdAt : new Date(),
      updatedAt: new Date(),
      timeCrossSell: {
        active: !!timeCrossSell.active,
        time: timeCrossSell.active ? Number(timeCrossSell.time || 0) : 0,
      },
    };

    if (isEditMode && promotionToEdit.id) {
      promotionData.id = promotionToEdit.id;
      if (onUpdatePromotion) {
        onUpdatePromotion(promotionData);
      }
    } else {
      onCreatePromotion(promotionData);
    }

    onClose();
  };

  const getSelectedProducts = () => {
    return activeTab === "trigger" ? triggerProducts : discountedProducts;
  };

  const isProductSelected = (productId) => {
    return getSelectedProducts().includes(productId);
  };

  // Classes CSS réutilisables pour le dark mode
  const borderColorClass = isDark ? "!border-gray-700" : "!border-gray-200";
  const bgColorClass = isDark ? "!bg-[#141414]" : "bg-white";
  const bgSecondaryClass = isDark ? "!bg-[#141414]" : "bg-gray-50";
  const textPrimaryClass = isDark ? "text-gray-100" : "text-gray-900";
  const textSecondaryClass = isDark ? "text-gray-400" : "text-gray-500";
  const hoverBgClass = isDark ? "hover:bg-gray-800" : "hover:bg-gray-50";
  const inputBgClass = isDark ? "bg-gray-800" : "bg-white";
  const inputBorderClass = isDark ? "!border-gray-600" : "!border-gray-300";

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel={
        isEditMode
          ? t("crossSellModal.header.editTitle")
          : t("crossSellModal.header.createTitle")
      }
    >
      <div className="flex flex-col h-full">
        {/* En-tête (inchangé) */}
        <div
          className={`flex-shrink-0 px-6 py-4 border-b ${borderColorClass} ${bgColorClass}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isEditMode ? (
                <Edit className="text-[#9b59bf]" size={24} />
              ) : (
                <Tag className="text-[#9b59bf]" size={24} />
              )}
              <div>
                <h2 className={`text-xl font-bold ${textPrimaryClass}`}>
                  {isEditMode
                    ? t("crossSellModal.header.editTitle")
                    : t("crossSellModal.header.createTitle")}
                </h2>
                <p className={`text-sm ${textSecondaryClass}`}>
                  {isEditMode
                    ? t("crossSellModal.header.editSubtitle")
                    : t("crossSellModal.header.createSubtitle")}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${hoverBgClass} rounded-full transition-colors`}
            >
              <X className={textSecondaryClass} size={22} />
            </button>
          </div>
        </div>

        {/* Contenu principal */}
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto p-6"
          style={{ maxHeight: "calc(85vh - 180px)" }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Section gauche : Configuration */}
            <div className="lg:col-span-1 space-y-6">
              {/* Titre et sous-titre (inchangé) */}
              <div className={`rounded-xl p-4 ${bgSecondaryClass}`}>
                <h3 className={`font-semibold ${textPrimaryClass} mb-4`}>
                  {t("cashBackModal.details.sectionTitle")}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium ${textSecondaryClass} mb-1`}
                    >
                      {t("crossSellModal.details.title")}
                      {"*"}
                    </label>
                    <input
                      type="text"
                      value={promotionTitle}
                      onChange={(e) => setPromotionTitle(e.target.value)}
                      placeholder={t("crossSellModal.details.placeholderTitle")}
                      className={`w-full px-4 py-2 border dark:!border-gray-700 ${inputBorderClass} rounded-lg ${inputBgClass} ${textPrimaryClass} focus:ring-2 focus:ring-[#9b59bf] focus:border-transparent`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium ${textSecondaryClass} mb-1`}
                    >
                      {t("crossSellModal.details.subtitle")}
                    </label>
                    <input
                      type="text"
                      value={promotionSubtitle}
                      onChange={(e) => setPromotionSubtitle(e.target.value)}
                      placeholder={t(
                        "crossSellModal.details.placeholderSubtitle"
                      )}
                      className={`w-full px-4 py-2 border dark:!border-gray-700 ${inputBorderClass} rounded-lg ${inputBgClass} ${textPrimaryClass} focus:ring-2 focus:ring-[#9b59bf] focus:border-transparent`}
                    />
                  </div>
                </div>
              </div>

              {/* Type de promotion (NOUVEAU) */}
              <div className={`rounded-xl p-4 ${bgSecondaryClass}`}>
                <h3 className={`font-semibold ${textPrimaryClass} mb-4`}>
                  {t("crossSellModal.promotionType.title")}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPromotionType("discount")}
                    className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg border dark:!border-gray-700 transition-all ${
                      promotionType === "discount"
                        ? "border-[#9b59bf] bg-purple-100 dark:bg-purple-900/30 text-[#9b59bf]"
                        : `${inputBorderClass} ${textSecondaryClass} hover:border-[#9b59bf] hover:text-[#9b59bf]`
                    }`}
                  >
                    <Percent size={24} />
                    <span>{t("crossSellModal.promotionType.discount")}</span>
                    <span className="text-xs opacity-70">
                      {t("crossSellModal.promotionType.discountDesc")}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPromotionType("freeShipping")}
                    className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg border dark:!border-gray-700 transition-all ${
                      promotionType === "freeShipping"
                        ? "border-green-600 bg-green-100 dark:bg-green-900/30 text-green-600"
                        : `${inputBorderClass} ${textSecondaryClass} hover:border-green-600 hover:text-green-600`
                    }`}
                  >
                    <Truck size={24} />
                    <span>
                      {t("crossSellModal.promotionType.freeShipping")}
                    </span>
                    <span className="text-xs opacity-70">
                      {t("crossSellModal.promotionType.freeShippingDesc")}
                    </span>
                  </button>
                </div>
              </div>

              {/* Type de réduction (conditionnel) */}
              {promotionType === "discount" && (
                <div className={`rounded-xl p-4 ${bgSecondaryClass}`}>
                  <h3 className={`font-semibold ${textPrimaryClass} mb-4`}>
                    {t("crossSellModal.discount.title")}
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setDiscountType("percentage")}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border dark:!border-gray-700 transition-all ${
                          discountType === "percentage"
                            ? "border-[#9b59bf] bg-purple-100 dark:bg-purple-900/30 text-[#9b59bf]"
                            : `${inputBorderClass} ${textSecondaryClass} hover:border-[#9b59bf] hover:text-[#9b59bf]`
                        }`}
                      >
                        <Percent size={18} />
                        <span>{t("crossSellModal.discount.percentage")}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setDiscountType("fixed")}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border dark:!border-gray-700 transition-all ${
                          discountType === "fixed"
                            ? "border-[#9b59bf] bg-purple-100 dark:bg-purple-900/30 text-[#9b59bf]"
                            : `${inputBorderClass} ${textSecondaryClass} hover:border-[#9b59bf] hover:text-[#9b59bf]`
                        }`}
                      >
                        <Tag size={18} />
                        <span>{t("crossSellModal.discount.fixed")}</span>
                      </button>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium ${textSecondaryClass} mb-1`}
                      >
                        {discountType === "percentage"
                          ? t("crossSellModal.discount.valueLabelPercentage")
                          : t("crossSellModal.discount.valueLabelFixed")}{" "}
                        *
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max={discountType === "percentage" ? "100" : "999999"}
                          value={discountValue}
                          onChange={(e) =>
                            setDiscountValue(
                              Math.max(0, Number(e.target.value))
                            )
                          }
                          className={`w-full px-4 py-2 border dark:!border-gray-700 ${inputBorderClass} rounded-lg ${inputBgClass} ${textPrimaryClass} focus:ring-2 focus:ring-[#9b59bf] focus:border-transparent`}
                        />
                        <div
                          className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${textSecondaryClass}`}
                        >
                          {discountType === "percentage" ? "%" : t("ui.dzd")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Case à cocher pour obliger la sélection (NOUVEAU) */}
              <div className={`rounded-xl p-4 ${bgSecondaryClass}`}>
                <h3 className={`font-semibold ${textPrimaryClass} mb-4`}>
                  {t("crossSellModal.conditions.title")}
                </h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={requireAllProducts}
                        onChange={(e) =>
                          setRequireAllProducts(e.target.checked)
                        }
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 border dark:!border-gray-700 rounded flex items-center justify-center transition-all ${
                          requireAllProducts
                            ? "bg-[#9b59bf] border-[#9b59bf]"
                            : inputBorderClass
                        }`}
                      >
                        {requireAllProducts && (
                          <Check size={12} className="text-white" />
                        )}
                      </div>
                    </div>
                    <div>
                      <div className={`font-medium ${textPrimaryClass}`}>
                        {t("crossSellModal.conditions.requireAllTitle")}
                      </div>
                      <div className={`text-xs ${textSecondaryClass}`}>
                        {t("crossSellModal.conditions.requireAllDesc")}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div className={`rounded-xl p-4 ${bgSecondaryClass}`}>
                <h3 className={`font-semibold ${textPrimaryClass} mb-4`}>
                  {t("crossSellModal.timer.title")}
                </h3>

                <div className="space-y-3">
                  {/* checkbox لتفعيل التايمر */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={timeCrossSell.active}
                        onChange={(e) =>
                          setTimeCrossSell((prev) => ({
                            ...prev,
                            active: e.target.checked,
                            // لو طفى التايمر صفر الوقت
                            time: e.target.checked ? prev.time : 0,
                          }))
                        }
                        className="sr-only"
                      />

                      <div
                        className={`w-5 h-5 border rounded flex items-center justify-center transition-all ${
                          timeCrossSell.active
                            ? "bg-[#9b59bf] border-[#9b59bf]"
                            : inputBorderClass
                        }`}
                      >
                        {timeCrossSell.active && (
                          <Check size={12} className="text-white" />
                        )}
                      </div>
                    </div>

                    <div>
                      <div className={`font-medium ${textPrimaryClass}`}>
                        {t("crossSellModal.timer.enableTitle")}
                      </div>
                      <div className={`text-xs ${textSecondaryClass}`}>
                        {t("crossSellModal.timer.enableDesc")}
                      </div>
                    </div>
                  </label>

                  {/* input الوقت يظهر فقط إذا active */}
                  {timeCrossSell.active && (
                    <div>
                      <label
                        className={`block text-sm font-medium ${textSecondaryClass} mb-1`}
                      >
                        {t("crossSellModal.timer.timeLabel")}
                      </label>

                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          value={timeCrossSell.time}
                          onChange={(e) =>
                            setTimeCrossSell((prev) => ({
                              ...prev,
                              time: Math.max(0, Number(e.target.value)),
                            }))
                          }
                          className={`w-full px-4 py-2 border ${inputBorderClass} rounded-lg ${inputBgClass} ${textPrimaryClass} focus:ring-2 focus:ring-[#9b59bf] focus:border-transparent`}
                        />

                        <div
                          className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${textSecondaryClass}`}
                        >
                          s
                        </div>
                      </div>

                      <p className={`text-xs mt-2 ${textSecondaryClass}`}>
                        {t("crossSellModal.timer.hint")}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Résumé mis à jour */}
              <div
                className={`border dark:!border-gray-700 rounded-xl p-4 ${
                  isDark
                    ? "bg-blue-900/20 border-blue-800"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <h4
                  className={`font-semibold mb-3 ${
                    isDark ? "text-blue-300" : "text-blue-700"
                  }`}
                >
                  {t("crossSellModal.summary.title")}
                </h4>

                <div className="flex items-center justify-center mb-3">
                  <div
                    className={`px-3 py-1 rounded-lg text-sm ${
                      isDark
                        ? "bg-blue-800 text-blue-300"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {triggerProducts.length} {t("settings.product")}
                  </div>
                  <div className="!mx-2 !flex-shrink-0">
                    {i18n.dir() === "rtl" ? (
                      <ArrowLeft
                        className="!text-purple-600 dark:!text-purple-400"
                        size={20}
                      />
                    ) : (
                      <ArrowRight
                        className="!text-purple-600 dark:!text-purple-400"
                        size={20}
                      />
                    )}
                  </div>
                  {promotionType === "discount" ? (
                    <div
                      className={`px-3 py-1 rounded-lg text-sm ${
                        isDark
                          ? "bg-green-800 text-green-300"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {discountedProducts.length} {t("settings.product")}
                    </div>
                  ) : (
                    <div
                      className={`px-3 py-1 rounded-lg text-sm ${
                        isDark
                          ? "bg-yellow-800 text-yellow-300"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      <Truck size={14} className="inline mr-1" />
                      {t("landing.suggestion_free_delivery")}
                    </div>
                  )}
                </div>

                <div className="text-sm space-y-2">
                  <div className={isDark ? "text-blue-400" : "text-blue-600"}>
                    <span className="font-medium">
                      {t("crossSellModal.summary.triggerProducts")}
                    </span>
                    <ul className="mt-1 space-y-1 max-h-20 overflow-y-auto">
                      {triggerProducts.slice(0, 5).map((id) => (
                        <li key={id} className="text-xs">
                          • {getProductName(id)}
                        </li>
                      ))}
                      {triggerProducts.length > 5 && (
                        <li className="text-xs">
                          {" "}
                          {t("crossSellModal.summary.more", {
                            count: triggerProducts.length - 5,
                          })}
                        </li>
                      )}
                    </ul>
                  </div>

                  {promotionType === "discount" && (
                    <div
                      className={isDark ? "text-green-400" : "text-green-600"}
                    >
                      <span className="font-medium">
                        {t("crossSellModal.summary.discountedProducts")}
                      </span>
                      <ul className="mt-1 space-y-1 max-h-20 overflow-y-auto">
                        {discountedProducts.slice(0, 5).map((id) => (
                          <li key={id} className="text-xs">
                            • {getProductName(id)}
                          </li>
                        ))}
                        {discountedProducts.length > 5 && (
                          <li className="text-xs">
                            {t("crossSellModal.summary.more", {
                              count: discountedProducts.length - 5,
                            })}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div
                    className={`pt-2 border-t ${
                      isDark ? "border-blue-800" : "border-blue-200"
                    }`}
                  >
                    {promotionType === "discount" ? (
                      <>
                        <span className={textSecondaryClass}>
                          {t("crossSellModal.summary.discount")}{" "}
                        </span>
                        <span className="font-semibold text-[#9b59bf]">
                          {discountValue}
                          {discountType === "percentage" ? "%" : t("ui.dzd")}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={textSecondaryClass}>{t("orderDetails.product_details.offer")} </span>
                        <span className="font-semibold text-green-600">
                          {t("landing.suggestion_free_delivery")}
                        </span>
                      </>
                    )}
                  </div>

                  {requireAllProducts && (
                    <div
                      className={`pt-2 border-t ${
                        isDark ? "border-blue-800" : "border-blue-200"
                      }`}
                    >
                      <span
                        className={`text-xs ${
                          isDark ? "text-yellow-400" : "text-yellow-600"
                        }`}
                      >
                        {t("ui.allProductsRequired")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section droite : Sélection des produits */}
            <div className="lg:col-span-2">
              <div
                className={`rounded-xl border dark:!border-gray-700 ${borderColorClass} h-full flex flex-col ${bgColorClass}`}
              >
                {/* Onglets mis à jour */}
                <div
                  className={`flex border-b ${borderColorClass} flex-shrink-0`}
                >
                  <button
                    onClick={() => setActiveTab("trigger")}
                    className={`flex-1 px-4 py-3 text-center font-medium transition-colors ${
                      activeTab === "trigger"
                        ? "bg-[#9b59bf] text-white"
                        : `${bgSecondaryClass} ${textSecondaryClass} hover:bg-gray-200 dark:hover:bg-gray-800`
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Tag size={18} />
                      <span>{t("crossSellModal.tabs.trigger")}</span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          activeTab === "trigger"
                            ? "bg-white/20"
                            : "bg-gray-300 dark:bg-gray-700"
                        }`}
                      >
                        {triggerProducts.length}
                      </span>
                    </div>
                    <p className="text-xs mt-1 opacity-80">
                      {t("crossSellModal.tabs.triggerDesc")}
                    </p>
                  </button>

                  <button
                    onClick={() => setActiveTab("discounted")}
                    className={`flex-1 px-4 py-3 text-center font-medium transition-colors ${
                      activeTab === "discounted"
                        ? promotionType === "discount"
                          ? "bg-green-600 text-white"
                          : "bg-green-600 text-white"
                        : `${bgSecondaryClass} ${textSecondaryClass} hover:bg-gray-200 dark:hover:bg-gray-800`
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {promotionType === "discount" ? (
                        <Percent size={18} />
                      ) : (
                        <Truck size={18} />
                      )}
                      <span>
                        {
                          promotionType === "discount"
                            ? t("crossSellModal.tabs.discounted") // ex: produits remisés
                            : t(
                                "crossSellModal.tabs.freeShippingTargets"
                              ) /* à ajouter dans i18n */
                        }
                      </span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          activeTab === "discounted"
                            ? "bg-white/20"
                            : "bg-gray-300 dark:bg-gray-700"
                        }`}
                      >
                        {discountedProducts.length}
                      </span>
                    </div>

                    <p className="text-xs mt-1 opacity-80">
                      {
                        promotionType === "discount"
                          ? t("crossSellModal.tabs.discountedDesc")
                          : t(
                              "crossSellModal.tabs.freeShippingTargetsDesc"
                            ) /* i18n */
                      }
                    </p>
                  </button>
                </div>

                {/* En-tête de la section produits */}
                <div
                  className={`p-4 border-b ${borderColorClass} flex-shrink-0`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${textPrimaryClass}`}>
                      {activeTab === "trigger"
                        ? t("crossSellModal.products.selectTrigger")
                        : promotionType === "discount"
                        ? t("crossSellModal.products.selectDiscounted")
                        : t("crossSellModal.products.applyToCart")}
                    </h3>
                    <button
                      onClick={toggleSelectAll}
                      className={`text-sm px-3 py-1 border dark:!border-gray-700 ${inputBorderClass} rounded-lg ${hoverBgClass} transition-colors`}
                    >
                      {getSelectedProducts().length === filteredProducts.length
                        ? t("crossSellModal.buttons.unselectAll")
                        : t("crossSellModal.buttons.selectAll")}
                    </button>
                  </div>

                  {/* Barre de recherche */}
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t("crossSellModal.search.placeholder")}
                      className={`w-full px-4 py-2 pl-10 border dark:!border-gray-700 ${inputBorderClass} rounded-lg ${inputBgClass} ${textPrimaryClass} focus:ring-2 focus:ring-[#9b59bf] focus:border-transparent`}
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Liste des produits */}
                <div
                  className="overflow-y-auto flex-1"
                  style={{ maxHeight: "300px" }}
                >
                  {filteredProducts.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      {t("crossSellModal.search.noResults")}
                    </div>
                  ) : (
                    <div
                      className={`divide-y ${
                        isDark ? "divide-gray-800" : "divide-gray-200"
                      }`}
                    >
                      {filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          className={`flex items-center justify-between p-4 transition-colors ${
                            isProductSelected(product.id)
                              ? activeTab === "trigger"
                                ? "bg-purple-50 dark:bg-purple-900/20"
                                : "bg-green-50 dark:bg-green-900/20"
                              : hoverBgClass
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() =>
                                activeTab === "trigger"
                                  ? toggleTriggerProduct(product.id)
                                  : toggleDiscountedProduct(product.id)
                              }
                              className={`w-5 h-5 rounded border dark:!border-gray-700 flex items-center justify-center transition-all ${
                                isProductSelected(product.id)
                                  ? activeTab === "trigger"
                                    ? "bg-[#9b59bf] border-[#9b59bf]"
                                    : "bg-green-600 border-green-600"
                                  : "border-gray-300 dark:border-gray-600"
                              }`}
                            >
                              {isProductSelected(product.id) && (
                                <Check size={12} className="text-white" />
                              )}
                            </button>

                            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
                              {product.image ? (
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              ) : (
                                <Tag size={24} className="text-gray-400" />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <h4
                                className={`font-medium ${textPrimaryClass} truncate`}
                              >
                                {truncateText(product.name, 65)}
                              </h4>
                            </div>
                          </div>

                          <div className="text-right flex-shrink-0 ml-4">
                            <div
                              className={`font-semibold ${textPrimaryClass}`}
                            >
                              {formatPrice(product.price)}
                            </div>
                            {discountValue > 0 &&
                              activeTab === "discounted" &&
                              promotionType === "discount" && (
                                <div className="text-sm">
                                  <span
                                    className={`${textSecondaryClass} line-through mr-2`}
                                  >
                                    {formatPrice(product.price)}
                                  </span>
                                  <span
                                    className={`font-medium ${
                                      discountType === "percentage"
                                        ? "text-green-600 dark:text-green-400"
                                        : "text-[#9b59bf]"
                                    }`}
                                  >
                                    {formatPrice(
                                      calculateDiscountedPrice(product.price)
                                    )}
                                  </span>
                                </div>
                              )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pied de page */}
        <div
          className={`flex-shrink-0 px-6 py-4 border-t ${borderColorClass} ${bgColorClass}`}
        >
          <div className="flex items-center justify-between">
            <div className={`text-sm ${textSecondaryClass}`}>
              {isEditMode
                ? t("crossSellModal.footer.editDesc")
                : t("crossSellModal.footer.createDesc")}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className={`px-5 py-2.5 border dark:!border-gray-700 ${inputBorderClass} ${textSecondaryClass} rounded-lg ${hoverBgClass} transition-colors font-medium`}
              >
                {t("crossSellModal.buttons.cancel")}
              </button>

              <button
                onClick={handleSubmitPromotion}
                disabled={
                  !promotionTitle.trim() ||
                  triggerProducts.length === 0 ||
                  (promotionType === "discount" &&
                    discountedProducts.length === 0)
                }
                className={`px-5 py-2.5 rounded-lg text-white font-medium flex items-center gap-2 shadow-sm transition-colors ${
                  !promotionTitle.trim() ||
                  triggerProducts.length === 0 ||
                  (promotionType === "discount" &&
                    discountedProducts.length === 0)
                    ? "bg-gray-400 dark:bg-gray-700 cursor-not-allowed"
                    : promotionType === "discount"
                    ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {isEditMode ? (
                  <Edit size={18} />
                ) : promotionType === "discount" ? (
                  <Tag size={18} />
                ) : (
                  <Truck size={18} />
                )}
                <span>
                  {isEditMode
                    ? t("crossSellModal.buttons.save")
                    : t("crossSellModal.buttons.create")}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PromotionModal;
